/* eslint-disable no-restricted-globals */
/* eslint-disable camelcase */
import _ from 'lodash';
import moment from 'moment-timezone';

import { DppFinal, TipoParto } from '../../../back/src/types';
import { Gestacao } from '../../../back/src/entity/Gestacao';
import { Parto } from '../../../back/src/entity/Parto';
import { avisosGestacao } from './constants';

// Filtra ignorando acentos
export function filterDeburr(queryText: string, itemText: string): boolean {
  const textDeburr = _.deburr(itemText).toLowerCase();
  const searchText = _.deburr(queryText).toLowerCase();
  return textDeburr.indexOf(searchText) > -1;
}

// Mask para decimais com vírgula
export function decimalBrMask(val: string | null, precision: number = 2): string | null {
  if (!val) {
    return null;
  }
  const number = (Number(val.replace(/\D/g, '')) / (10 ** precision));
  if (number >= 0) {
    return number.toFixed(precision).replace('.', ',');
  }
  return null;
}

// Recebe data no formado DD/MM/YYYY e retorna Date ou null
export function dataBrToDate(dataBr: string): Date | null {
  const dateString = dataBr.split('/').reverse().join('-');
  const momentDate = moment(dateString);
  if (momentDate.isValid()) {
    return momentDate.toDate();
  }
  return null;
}

// Recebe altura em metros formatada em decimal BR e retorna altura em cm
export function alturaCm(alturaM: string): number | null {
  if (!alturaM) {
    return null;
  }
  const alturaMNumber = Number(alturaM.replace(',', '.'));
  if (alturaMNumber > 0) {
    return Math.round(alturaMNumber * 100);
  }
  return null;
}

// Recebe peso em kg formatado em decimal BR e retorna peso em gramas
export function pesoGramas(pesoKg: string): number | null {
  if (!pesoKg) {
    return null;
  }
  const pesoKgNumber = Number(pesoKg.replace(',', '.'));
  if (pesoKgNumber > 0) {
    return Math.round(pesoKgNumber * 1000);
  }
  return null;
}

// Cálculo da paridade
export function calculoParidade(gestacoes: Gestacao[]): string {
  let paridade: string = '';
  const numGestacoes: number = gestacoes.length;
  const numAbortos: number = _.filter(
    gestacoes,
    gestacao => gestacao.parto && gestacao.parto.tipo_parto === TipoParto.A,
  ).length;
  const numNormais: number = _.filter(
    gestacoes,
    gestacao => gestacao.parto && gestacao.parto.tipo_parto === TipoParto.N,
  ).length;
  const numCesareas: number = _.filter(
    gestacoes,
    gestacao => gestacao.parto && gestacao.parto.tipo_parto === TipoParto.C,
  ).length;

  if (numGestacoes === 1) {
    const gestacao = _.first(gestacoes);
    if (gestacao
      && !gestacao.ended
      && !gestacao.parto
      && !gestacao.primeira_gestacao
    ) {
      return '?';
    }
  }
  if (numGestacoes > 0) {
    paridade = paridade.concat(`G${numGestacoes}`);
  }
  if (numAbortos > 0) {
    paridade = paridade.concat(`A${numAbortos}`);
  }
  if (numNormais > 0) {
    paridade = paridade.concat(`PN${numNormais}`);
  }
  if (numCesareas > 0) {
    paridade = paridade.concat(`C${numCesareas}`);
  }

  return paridade;
}

// DPP
export function getDpp(gestacao: Gestacao | null | undefined) {
  if (!gestacao) {
    return null;
  }
  if (gestacao.dpp_final === DppFinal.M) {
    if (!gestacao.data_ultima_menstruacao) {
      return null;
    }
    return moment(gestacao.data_ultima_menstruacao).utc().set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    }).add(40, 'w');
  }
  if (gestacao.dpp_final === DppFinal.U) {
    if (!gestacao.data_provavel_parto_us) {
      return null;
    }
    return moment(gestacao.data_provavel_parto_us).utc().set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    });
  }
  return null;
}

// Cálculo idade gestacional
export function calculoIdadeGestacional(
  gestacao: Gestacao | null | undefined,
  dataReferencia: Date | string | null = null,
  full: boolean = false,
): string {
  if (!gestacao) {
    return '';
  }
  let momentDate: moment.Moment;
  if (gestacao.dpp_final === DppFinal.M) {
    if (!gestacao.data_ultima_menstruacao) {
      return '';
    }
    momentDate = moment(gestacao.data_ultima_menstruacao).utc().set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    });
  } else if (gestacao.dpp_final === DppFinal.U) {
    if (!gestacao.data_provavel_parto_us) {
      return '';
    }
    momentDate = moment(gestacao.data_provavel_parto_us).utc().set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    }).subtract(40, 'w');
  } else {
    return '';
  }
  if (!momentDate.isValid()) {
    return 'DPP inválida';
  }
  let momentDataReferencia: moment.Moment;
  if (!dataReferencia) {
    momentDataReferencia = moment().set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    });
  } else {
    momentDataReferencia = moment(dataReferencia).set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    });
  }
  if (!momentDataReferencia.isValid()) {
    return 'Erro';
  }
  if (gestacao.parto && gestacao.parto.data_parto) {
    const daysDesdeParto = momentDataReferencia.diff(moment(gestacao.parto.data_parto), 'days');
    if (daysDesdeParto > 0) {
      momentDataReferencia = moment(gestacao.parto.data_parto);
    }
  }
  const days = momentDataReferencia.diff(momentDate, 'days');
  if (days < 0) {
    return 'DPP inválida';
  }
  return `${Math.floor(days / 7)} ${days % 7 > 0 ? `${days % 7}/7` : ''}${full ? ' semanas' : ''}`;
}

// Monta string com ocorrencias de parto
export function ocorrenciasPartoString(parto: Parto) {
  if (!parto) return '';
  let ocorrenciasParto = '';
  if (parto.ocorrencia_analgesia) {
    ocorrenciasParto = `${ocorrenciasParto}Analgesia, `;
  }
  if (parto.ocorrencia_forceps) {
    ocorrenciasParto = `${ocorrenciasParto}Forceps, `;
  }
  if (parto.ocorrencia_hpp) {
    ocorrenciasParto = `${ocorrenciasParto}HPP(${parto.hpp_quantidade}), `;
  }
  if (parto.ocorrencia_induzido) {
    ocorrenciasParto = `${ocorrenciasParto}Induzido, `;
  }
  if (parto.ocorrencia_vacuo) {
    ocorrenciasParto = `${ocorrenciasParto}Vácuo, `;
  }
  if (parto.ocorrencia_ruptura) {
    ocorrenciasParto = `${ocorrenciasParto}Ruptura artificial da bolsa, `;
  }
  if (parto.ocorrencia_ocitocina) {
    ocorrenciasParto = `${ocorrenciasParto}Ocitocina, `;
  }
  if (parto.ocorrencia_episiotomia) {
    ocorrenciasParto = `${ocorrenciasParto}Episiotomia, `;
  }
  if (parto.ocorrencia_uti_materna) {
    ocorrenciasParto = `${ocorrenciasParto}UTI, `;
  }
  if (parto.ocorrencia_kristeller_pressao_fundica) {
    ocorrenciasParto = `${ocorrenciasParto}Kristeller ou pressão fúndica, `;
  }
  if (parto.ocorrencia_transfusao) {
    ocorrenciasParto = `${ocorrenciasParto}Transfusão, `;
  }
  if (parto.ocorrencia_distocia_ombros) {
    ocorrenciasParto = `${ocorrenciasParto}Distócia de ombros, `;
  }
  if (ocorrenciasParto.length > 2) {
    ocorrenciasParto = ocorrenciasParto.substring(0, ocorrenciasParto.length - 2);
  }
  return ocorrenciasParto;
}

export function montaFlagsAarray(gestacao: Gestacao): {
  sigla: string,
  descricao: string,
  color: string,
}[] {
  if (!gestacao) {
    return [];
  }
  const flags = [] as { sigla: string, descricao: string, color: string }[];
  if (gestacao.gestacao_solo) {
    flags.push(avisosGestacao.MS);
  }
  if (gestacao.gestacao_gemelar) {
    flags.push(avisosGestacao.GG);
  }
  if (gestacao.flag_pa) {
    flags.push(avisosGestacao.PA);
  }
  if (gestacao.flag_dg) {
    flags.push(avisosGestacao.DG);
  }
  if (gestacao.flag_rc) {
    flags.push(avisosGestacao.RC);
  }
  if (gestacao.flag_hg) {
    flags.push(avisosGestacao.HG);
  }
  if (gestacao.flag_rh_negativo) {
    flags.push(avisosGestacao.RH);
  }
  if (gestacao.flag_ar) {
    flags.push(avisosGestacao.AR);
  }
  if (gestacao.flag_ht) {
    flags.push(avisosGestacao.HT);
  }
  if (gestacao.flag_tf) {
    flags.push(avisosGestacao.TF);
  }
  return flags;
}


export function calculaImc(peso_gramas: number, altura_cm: number) {
  const pesoKg = peso_gramas / 1000;
  if (isNaN(pesoKg) || pesoKg <= 0) return null;
  const alturaM = altura_cm / 100;
  if (isNaN(alturaM) || alturaM <= 0) return null;
  const imc = pesoKg / (alturaM * alturaM);
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(imc);
}
